import { API } from "@swivl-mono/api-helper";
import { ConversationTimeline as SharedTimeline } from "@swivl-mono/chat-ui";
import { ConversationMessage } from "@swivl-mono/types";
import { Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { AnonymousAccessNavBar } from "../../Components/AnonymousAccessNavBar/AnonymousAccessNavBar";
import ErrorView from "../../Components/ErrorView/ErrorView";
import { useAccessOrgRedirect } from "../../Hooks/useAccessOrgRedirect";
import { mapCCMessage } from "../../Models/ContactCenterV3/ContactCenterV3.Store";
import { ChannelTabBar } from "../ContactCenterV3/Shared/ChannelTabBar";
import { TicketPanelLayout } from "../Ticketing/components/TicketPanelLayout";

interface TranscriptChannel {
  channel: string;
  botConversationId: number;
  summary: string;
  messages: ConversationMessage[];
}

interface TranscriptState {
  orgId: number;
  userId: number;
  userName: string;
  locationName: string;
  locationAddress: string;
  channels: TranscriptChannel[];
}

// The "View Transcript" link — read-only, this session's channels only.
export default function TranscriptAccessView() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<TranscriptState | null>(null);
  const [activeChannel, setActiveChannel] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loadedToken, setLoadedToken] = useState<string | null>(null);
  const hasLoaded = loadedToken === token;

  useEffect(() => {
    setState(null);
    setActiveChannel(null);
    setError(false);
    setLoadedToken(null);
    if (!token) return;
    API.ContactCenterV3Access.getTranscript(token)
      .then((res) => {
        const channels = res.channels.map((ch) => ({
          channel: ch.channel,
          botConversationId: ch.botConversationId,
          summary: ch.summary,
          messages: ch.messages.map((m) => mapCCMessage(m, ch.botConversationId))
        }));
        setState({
          orgId: res.orgId,
          userId: res.userId,
          userName: res.userName,
          locationName: res.locationName,
          locationAddress: res.locationAddress,
          channels
        });
        setActiveChannel(channels[0]?.channel ?? null);
      })
      .catch(() => setError(true))
      .finally(() => setLoadedToken(token));
  }, [token]);

  const realPath = state ? `/contact-center-v3/conversations/${state.userId}` : null;
  const { showSpinner } = useAccessOrgRedirect({ hasLoaded, resourceOrgId: state?.orgId, realPath, resourceLabel: "This conversation" });

  const activeTab = state?.channels.find((ch) => ch.channel === activeChannel);

  return (
    <div className="flex h-screen flex-col">
      <AnonymousAccessNavBar />
      {error ? (
        <div className="flex flex-1 items-center justify-center bg-grey-50">
          <ErrorView />
        </div>
      ) : showSpinner || !state ? (
        <div className="flex flex-1 items-center justify-center bg-grey-50">
          <Spinner />
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,auto)_1fr] gap-3 bg-grey-50 p-3 lg:grid-cols-[320px_1fr] lg:grid-rows-none">
          {/* Mobile: caps at half height, scrolls past that. */}
          <div className="max-h-[50vh] min-h-0 overflow-hidden rounded-lg border border-grey-200 bg-white lg:max-h-none">
            <TicketPanelLayout headerLeft={<span className="heading-base-alt text-black">Customer</span>}>
              <div className="flex min-h-0 flex-col gap-3 overflow-auto p-4">
                <div>
                  <span className="body-sm-alt text-grey-500">Name</span>
                  <p className="body-base-alt text-black">{state.userName || "Unknown"}</p>
                </div>
                {state.locationName && (
                  <div>
                    <span className="body-sm-alt text-grey-500">Location</span>
                    <p className="body-base-alt text-black">{state.locationName}</p>
                    {state.locationAddress && <p className="body-sm text-grey-600">{state.locationAddress}</p>}
                  </div>
                )}
                {activeTab?.summary && (
                  <div>
                    <span className="body-sm-alt text-grey-500">Summary</span>
                    <p className="body-sm text-grey-700">{activeTab.summary}</p>
                  </div>
                )}
              </div>
            </TicketPanelLayout>
          </div>

          <div className="min-h-0 overflow-hidden rounded-lg border border-grey-200 bg-white">
            <TicketPanelLayout
              headerLeft={
                <ChannelTabBar
                  activeChannel={activeChannel ?? "web"}
                  onSelect={setActiveChannel}
                  isSelectable={(ch) => state.channels.some((c) => c.channel === ch)}
                />
              }
            >
              <SharedTimeline
                messages={activeTab?.messages ?? []}
                userName={state.userName}
                palette="console"
                ownRole="assistant"
                emptyMessage="No messages in this conversation"
              />
            </TicketPanelLayout>
          </div>
        </div>
      )}
    </div>
  );
}
